import { Component, DestroyRef, inject, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { filter, take } from 'rxjs/operators';

import { AuthService } from '@auth/repository';
import { sessionUser$ } from '@auth/repository/auth.repository';
import type { IEntityLookupBean } from '@core/models/app.model';
import type { ItemInfo } from '@core/models/navigation-item.model';
import type { IUserProfile, IUserSession } from '@core/models/user.model';
import { PermissionService } from '@core/permissions/permission.service';
import { DIALOG_SIZE } from '@shared/constants/ume-constants';
import { SelectEntityDialogComponent } from '@shared/dialogs/select-entity-dialog/select-entity-dialog.component';
import { CompanyTypePipe } from '@shared/pipes/company-type.pipe';
import { SanitizeSrcPipe } from '@shared/pipes/sanitize-src.pipe';
import { UserService } from '@user/repository';
import { profile$ } from '@user/repository/user.repository';
import { UmeFlagComponent } from '@widgets/ume-flag/ume-flag.component';

import { getProviderItems, getUserItems } from '../navigation/navigation-items.helpers';

@Component({
  selector: 'ume-profile-menu',
  templateUrl: './profile-menu.component.html',
  styleUrls: ['./profile-menu.component.scss'],
  imports: [
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    CompanyTypePipe,
    SanitizeSrcPipe,
    RouterLink,
    UmeFlagComponent,
  ],
})
export class ProfileMenuComponent implements OnInit {
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly authService: AuthService = inject(AuthService);
  private readonly permissions: PermissionService = inject(PermissionService);
  private readonly userService: UserService = inject(UserService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly currentUser = signal<IUserSession | null>(null);
  readonly userCompaniesToShow = signal<IEntityLookupBean[]>([]);
  readonly selectedCompany = signal<IEntityLookupBean | null>(null);
  readonly menuItems = signal<ItemInfo[]>([]);
  readonly firstName = signal('');
  readonly fullName = signal('');
  readonly photoURL = signal<string | null>(null);
  readonly picLoadError = signal(false);
  readonly showThreshold = signal(10);

  ngOnInit(): void {
    this.createNavItems();

    // load data for the first time
    this.userService.getMyProfile().pipe(take(1)).subscribe();

    profile$
      .pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef))
      .subscribe(({ firstName, lastName, picture }: IUserProfile) => {
        this.firstName.set(firstName);
        this.fullName.set(`${firstName} ${lastName}`);
        this.photoURL.set(picture || null);
      });

    sessionUser$
      .pipe(
        filter((user: IUserSession) => Boolean(user.selectedCompany)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((user: IUserSession) => {
        this.currentUser.set(user);
        this.selectedCompany.set(user.selectedCompany);
        this.userCompaniesToShow.set([...user.linkedCompanies]);
        this.firstName.set(user.firstName);
        this.fullName.set(`${user.firstName} ${user.lastName}`);

        this._prepareCompaniesToShow();
        this.createNavItems();
      });
  }

  public changeCompany(company: IEntityLookupBean): void {
    if (company.guid !== this.currentUser()?.selectedCompany?.guid) {
      this.authService.redirectUserToCompany(company);
      company.isNew = false;
    }
  }

  public showMoreCompanies(): void {
    this.dialog
      .open(SelectEntityDialogComponent, {
        data: {
          label: 'Choose your current entity',
          showClose: true,
        },
        width: DIALOG_SIZE.MODAL_SM_UP,
        minHeight: '320px',
      })
      .afterClosed()
      .pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef))
      .subscribe((company: IEntityLookupBean) => {
        this.changeCompany(company);
      });
  }

  public loadError(): void {
    this.picLoadError.set(true);
  }

  public logOut(): void {
    this.authService.logout();
  }

  private _prepareCompaniesToShow(): void {
    const selected = this.selectedCompany();
    if (!selected) {
      return;
    }

    // show currently selected company on top of the list
    const companies = this.userCompaniesToShow().filter((c) => c.guid !== selected.guid);
    companies.unshift(selected);

    this.userCompaniesToShow.set(companies.slice(0, this.showThreshold()));
  }

  private createNavItems(): void {
    this.menuItems.set(this.permissions.isUmeOps() ? getProviderItems() : getUserItems());
  }
}
